import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/YoutubeAnalyticsShorts.graphql';
import { Query, QueryyoutubeAnalyticsShortsArgs } from '../../__generated__/globalTypes';

export const useYoutubeAnalyticsShortsQuery = (options?: QueryHookOptions<Query, QueryyoutubeAnalyticsShortsArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryyoutubeAnalyticsShortsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useYoutubeAnalyticsShortsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryyoutubeAnalyticsShortsArgs>
): QueryResult<Query, QueryyoutubeAnalyticsShortsArgs> & {
  getYoutubeAnalyticsShorts: LazyQueryExecFunction<Query, QueryyoutubeAnalyticsShortsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getYoutubeAnalyticsShorts, result] = useLazyQuery<Query, QueryyoutubeAnalyticsShortsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getYoutubeAnalyticsShorts, ...result };
};

export const useYoutubeAnalyticsShortsPromiseQuery = () => {
  const client = useApolloClient();

  const getYoutubeAnalyticsShorts = useCallback(
    (options?: Omit<QueryOptions<QueryyoutubeAnalyticsShortsArgs, Query>, 'query'>) =>
      client.query<Query, QueryyoutubeAnalyticsShortsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getYoutubeAnalyticsShorts };
};
// Suspense Query Hooks
export const useYoutubeAnalyticsShortsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryyoutubeAnalyticsShortsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryyoutubeAnalyticsShortsArgs>(QUERY, {
    ...options
  });

  return result;
};
